import { Data } from 'emoji-mart';
import { saveAs } from 'file-saver';
import { json2csv } from 'json-2-csv';
import * as _ from 'lodash';
import moment from 'moment';
import slackMessageParser, { Node, NodeType } from 'slack-message-parser';
import { validate as validateUuid } from 'uuid';

import {
  CONVERSATION_PRIORITY_VALUES,
  CUSTOM_FIELD_TYPE_USER_ATTRIBUTE,
  DATE_OPTION_LABELS,
  DEFAULT_PRIORITY_LABEL_MAP_FORM,
  DEFAULT_PRIORITY_TOKENS,
  EXTERNAL_TASK_STATE,
  MESSAGE_SOURCE_TYPE,
  OPTION_LABEL_TO_KEY,
  OUTBOUND_RECIPIENT_TYPE,
  PRIVATE_CHANNEL_NAME,
  RELATIVE_DATE_OPTIONS,
  routes,
} from 'src/app/constants';
import { environment } from 'src/environments/environment';
import {
  Conversation,
  CUSTOM_FIELD_TYPES,
  Customer,
  Organizer,
  PriorityTokens,
  Profile,
  SIMPLE_CONDITIONS_OPERATORS,
  SlackChannel,
  Upload,
  WhereClause,
} from 'src/models';
import { ApiService } from 'src/services/api.service';

import { CURRENT_USER_SENTINEL, FILTER_OPERATORS, FilterOption, SelectedFilterMap } from './filter-utils';

export function fillPriorityLabelMapFormWithCurrentValues(
  currentPriorityMap: Record<string, Partial<PriorityTokens> | null> | undefined,
): Record<string, PriorityTokens> {
  const newForm = _.cloneDeep(DEFAULT_PRIORITY_LABEL_MAP_FORM);

  if (currentPriorityMap) {
    for (const key of ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']) {
      const loadedTokens = currentPriorityMap[key];
      if (loadedTokens) {
        // if loadedTokens is not null or undefined
        newForm[key] = {
          LABEL: loadedTokens.LABEL ?? null,
          EMOJI: loadedTokens.EMOJI ?? null,
          DESCRIPTION: loadedTokens.DESCRIPTION ?? null,
        };
      }
      // If loadedTokens is null or undefined for a key,
      // newForm[key] retains its default shape from DEFAULT_PRIORITY_LABEL_MAP_FORM_SHAPE
    }
  }
  return newForm;
}

const MESSAGE_TYPE_LABELS: { [key: string]: string } = {
  [MESSAGE_SOURCE_TYPE.DASHBOARD]: 'Dashboard',
  [MESSAGE_SOURCE_TYPE.EMAIL]: 'Email',
  [MESSAGE_SOURCE_TYPE.WIDGET]: 'Widget',
  [MESSAGE_SOURCE_TYPE.TEAMS]: 'Microsoft Teams',
  [MESSAGE_SOURCE_TYPE.SLACK]: 'Slack',
  [MESSAGE_SOURCE_TYPE.SLACK_BOT_DM]: 'Slack Bot DM',
  [MESSAGE_SOURCE_TYPE.AUTOMATION]: 'Automation',
};

const replacer = (value: any) => {
  if (typeof value === 'boolean') {
    return value.toString();
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (_.isEmpty(value)) {
    return '';
  }

  if (value instanceof Date) {
    return moment(value).format();
  }

  if (value instanceof Array) {
    return JSON.stringify(value).replace(/,/g, ' ');
  }

  return value;
};

export const downloadCSV = async (
  items: any[],
  columns: string[] | { id: string; label: string }[],
  filename: string,
) => {
  if (!items.length) {
    return;
  }

  const normalizedColumns = columns.map((column) => {
    if (typeof column === 'string') {
      return { field: column, title: column };
    }

    return { field: column.id, title: column.label };
  });

  const blob = new Blob(
    [
      await json2csv(
        items.map((item) =>
          Array.from(Object.entries(item)).reduce((acc, [key, value]) => {
            acc[key] = replacer(value);

            return acc;
          }, {}),
        ),
        {
          keys: normalizedColumns,
        },
      ),
    ],
    { type: 'text/csv' },
  );

  saveAs(blob, `${filename}.csv`);
};

export const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return null;
  }
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
};

export const validateEmail = (email?: string) => {
  if (!email) {
    return;
  }

  const cleanEmail = email
    .toLowerCase()
    .replace(/^\s+|\s+$/g, '')
    .replace(',', '');

  if (!/(.+)@(.+){1,}\.(.+){1,}/.test(cleanEmail)) {
    return;
  }

  return cleanEmail;
};

export const durationStringFromMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const mins = minutes - h * 60;
  const minSuffix = mins === 0 ? `` : `${mins} min`;
  const hourPlurality = h > 1 ? 's ' : ' ';
  const hourPrefix = h === 0 ? `` : `${h} hour${hourPlurality}`;
  return `${hourPrefix}${minSuffix}`;
};

export const getHtmlPreviewText = (text: string, length = 220) => {
  if (!text) {
    return '';
  }

  const htmlFree = text
    .replace(/<div>/g, ' ')
    .replace(/<br>/g, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&#160;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&#34;/g, '"');
  const substring = htmlFree.substring(0, 220);
  return substring.length >= length ? `${substring}...` : substring;
};

export function getCompanyCustomFieldId(field) {
  if (field.type === CUSTOM_FIELD_TYPE_USER_ATTRIBUTE) {
    return getAdditionalIdpFieldId(field.fieldKey);
  }

  return getCustomFieldId(field);
}

export function getCustomFieldId(field) {
  return `customFields.${field.id ?? _.camelCase(field.label)}`;
}

export function getAdditionalIdpFieldId(fieldName) {
  return `additionalIdpFields.${fieldName}`;
}

export function flattenAdditionalIdpFields(additionalIdpFields: Record<string, unknown>, prefix: string[] = []) {
  return Object.entries(additionalIdpFields).reduce((acc, [key, value]) => {
    // Exclude array fields for now.
    // TODO: Maybe figure out a way to handle these at some point.
    if (Array.isArray(value)) {
      return acc;
    }

    const newPrefix = [...prefix, key];

    if (typeof value === 'object' && value !== null) {
      return {
        ...acc,
        ...flattenAdditionalIdpFields(value as Record<string, unknown>, newPrefix),
      };
    }

    return {
      ...acc,
      [newPrefix.join('.')]: value,
    };
  }, {});
}

export function removeUndefinedFields(data: Record<string, any>) {
  return _.omitBy(data, (v) => v === undefined);
}

export function isDev(): boolean {
  return environment.environment === 'dev';
}

export function getFirebaseSettings() {
  const isDev = environment.environment === 'dev';
  const firebaseSettings = { ...environment.firebase };

  if (!isDev) {
    if (getAppConfig().useLegacyAuthDomain) {
      firebaseSettings.authDomain = `auth.${environment.rootDomain}`;
    } else {
      firebaseSettings.authDomain = window.location.host;
    }
  }

  return firebaseSettings;
}

export interface Recipient {
  type?: typeof OUTBOUND_RECIPIENT_TYPE.COMMUNITY | typeof OUTBOUND_RECIPIENT_TYPE.EMAIL;
  id?: string;
  email?: string;
  name?: string;
  count?: number;
}

export function createCommunityRecipient(community: Organizer): Recipient {
  return {
    id: community.objectId ?? community.id ?? null,
    name: `${community.name} (${community.followerCount} member${community.followerCount === 1 ? '' : 's'})`,
    type: OUTBOUND_RECIPIENT_TYPE.COMMUNITY,
    count: community.followerCount,
  };
}

export function createCustomerRecipient(customer: Partial<Customer>): Recipient {
  return {
    id: customer.id ?? null,
    name: `${customer.name} (${customer.followerCount} member${customer.followerCount === 1 ? '' : 's'})`,
    type: OUTBOUND_RECIPIENT_TYPE.COMMUNITY,
    count: customer.followerCount,
  };
}

export function createUserRecipient(user: Profile): Recipient {
  return {
    id: user.id,
    name: `${user.name} (${user.email ?? user.phoneNumber})`,
    type: OUTBOUND_RECIPIENT_TYPE.USER,
  };
}

export function isUrl(str: string) {
  str = str.trim();

  if (!str.startsWith('https://') && !str.startsWith('http://')) {
    str += 'https://';
  }

  try {
    new URL(str);

    return true;
  } catch {
    return false;
  }
}

export function isHtml(str: string): boolean {
  if (!str) {
    return false;
  }
  // A simple regex to check for the presence of HTML tags.
  // This looks for <tagname ...> or </tagname>
  const htmlRegex = /<([a-z][a-z0-9]*)\b[^>]*>|<\/([a-z][a-z0-9]*)\b[^>]*>/i;
  return htmlRegex.test(str);
}

export function findMedian(arr: number[]) {
  if (!arr.length) {
    return undefined;
  }

  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);

  return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
}

export function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

export function getHtmlFromSlackMd(message: string) {
  const parsed = slackMessageParser(message);

  const _toHtml = (node: Node, isRoot: boolean): string => {
    switch (node.type) {
      case NodeType.Root:
        return `${node.children.map((c) => _toHtml(c, isRoot)).join('')}`;
      case NodeType.Text:
        if (isRoot) {
          return _.escape(_.unescape(node.text));
        }

        return _.escape(_.unescape(node.text));
      case NodeType.Bold:
        return `<strong>${node.children.map((c) => _toHtml(c, false)).join('')}</strong>`;
      case NodeType.Italic:
        return `<i>${node.children.map((c) => _toHtml(c, false)).join('')}</i>`;
      case NodeType.Strike:
        return `<del>${node.children.map((c) => _toHtml(c, false)).join('')}</del>`;
      case NodeType.URL:
        return `<a href="${_.escape(node.url)}" target="_blank">${
          node.label ? node.label.map((c) => _toHtml(c, false)).join('') : _.escape(node.url)
        }</a>`;
      case NodeType.Code: {
        return `<code class="code">${node.text}</code>`;
      }
      case NodeType.PreText: {
        // replace the opening and closing new line characters following triple backticks
        return `<pre class="code">${node.text.replace(/^\n|\n$/g, '')}</pre>`;
      }
      case NodeType.Emoji:
        const emoji = Data?.emojis[node.name]?.skins?.[0];

        if (emoji?.native) {
          return emoji.native;
        } else if (emoji?.src) {
          return `<img src="${emoji.src}" alt="${node.name}" class="custom-emoji" />`;
        }

        return _.escape(node.source);
      case NodeType.Quote:
        return `<blockquote class="blockquote">${node.children.map((c) => _toHtml(c, false)).join('')}</blockquote>`;
      case NodeType.UserLink:
        // @ts-ignore
        return `<span class="mention-tag">@${_.escape(node.label?.[0]?.text ?? node.userID)}</span>`;
      case NodeType.ChannelLink:
        // @ts-ignore
        return `<span class="mention-tag">#${_.escape(node.label?.[0]?.text ?? node.channelID)}</span>`;
      case NodeType.Command:
        return `<span class="code">@${_.escape(node.name ?? 'Command')}</span>`;
      default:
        // @ts-ignore
        return _.escape(node.source);
    }
  };

  return `<p>${_toHtml(parsed, true)}</p>`.replace(/\n/g, '<br/>').trim();
}

export function formatUsername(contact: { name?: string | null; email?: string | null }) {
  return contact.name || contact.email;
}

export function formatLink(userId: string, isExternal: boolean) {
  return `/dashboard/${routes.PEOPLE}${isExternal ? '/external' : ''}/${userId}`;
}

function replaceEmoji(text: string): string {
  return text.replace(/:(.+?):/g, (match, p1) => Data?.emojis[p1]?.skins?.[0]?.native ?? match);
}

export function formatSlackContent(resolvedContent: any[] = []) {
  if (!resolvedContent.length) {
    return '(no message content)';
  }

  return resolvedContent.reduce((acc, next) => {
    if (typeof next === 'object') {
      if (next.type === 'user') {
        return acc + `@${_.escape(next.name ?? 'User')}`;
      } else if (next.type === 'channel') {
        return acc + `#${_.escape(next.name ?? 'channel')}`;
      } else {
        return acc + `${_.escape(next.name ?? next.id ?? 'link')}`;
      }
    }

    return acc + replaceEmoji(_.unescape(next));
  }, '');
}

export function formatConversationContent(conversation: Conversation, maxLength?: number) {
  const formattedContent = formatSlackContent(conversation.resolvedTitle);

  if (maxLength != null) {
    return formattedContent.length > maxLength ? formattedContent.substring(0, maxLength) + '...' : formattedContent;
  }

  return formattedContent;
}

export function transformMessage(message: any) {
  if (!message) {
    return message;
  }

  const user = message.sentByUser ?? message.user;
  const userId = user?.id;
  const widgetSession = message.inbound?.widgetMessage?.widgetSession;
  let name = null;
  let link = null;
  let image = null;
  const type = MESSAGE_TYPE_LABELS[message.sourceType] ?? MESSAGE_TYPE_LABELS.SLACK;
  let userType = 'customer';
  let isVerified = true;
  let email = null;

  if (user) {
    name = formatUsername(user);
    email = user.email;
    link = formatLink(user.id, user.isExternal);
    image = user.photo;

    if (!user.isExternal) {
      if (user.slackChannelMember?.isExternal) {
        userType = 'internal_customer';
      } else {
        userType = 'internal';
      }
    }
  } else if (message.botId) {
    name = message.botUsername ?? message.botName;
    image = message.botImage ?? undefined;
    userType = 'bot';
  }

  if (message.sourceType === MESSAGE_SOURCE_TYPE.EMAIL) {
    isVerified = false;
  } else if (message.sourceType === MESSAGE_SOURCE_TYPE.WIDGET) {
    isVerified = widgetSession?.isVerified ?? false;
  }

  if (!name) {
    name = 'No name';
  }

  return {
    ...message,
    userId,
    name,
    email,
    image,
    link,
    userType,
    type,
    isVerified,
    botId: message.botId,
    editedAt: message.editedAt ? new Date(message.editedAt) : undefined,
    editedBy: message.editedBy,
  };
}

export function delay(ms: number) {
  return new Promise((resolve) => {
    setTimeout(() => resolve(undefined), ms);
  });
}

interface AppConfig {
  thirdPartyCookiesEnabled?: boolean;
  subdomain?: string;
  tenantId?: string;
  logo?: string;
  primaryColor?: string;
  authProviders?: string[];
  domains?: string[];
  homePageFilters?: string[];
  analyticsDisabled: boolean;
  phoneNumberRequirement: string;
  customProfileFields?: any[];
  useLegacyAuthDomain: boolean;
  workos?: {
    directoryId?: string;
  };
  defaultCommunityId?: string;
  defaultFieldSettings?: Record<string, { public?: boolean }>;
  permissions?: {
    canCreatePlans?: boolean;
    canCreatePosts?: boolean;
    canCreateOrganizers?: boolean;
    canViewPeople?: boolean;
  };
  type?: string;
  slackTeamId?: string;
  slackTeamName?: string;
  accountStatus?: string;
  emailSupport?: {
    enabled?: boolean;
    slackChannelId?: string;
  };
  googleAnalyticsTrackingId?: string;
  slackClientId?: string;
  slackAppId?: string;
}

let appConfig: AppConfig | undefined;

export function getAppConfig() {
  if (!appConfig) {
    const inlineJsonElement = document.querySelector(
      `script[type="application/json"][data-selector="unthread-config"]`,
    );

    appConfig = JSON.parse(inlineJsonElement.textContent);
  }

  return appConfig;
}

export function getCspNonce() {
  const nonce = (document.querySelector('meta[name="CSP_NONCE"]') as HTMLMetaElement)?.content;

  return nonce;
}

export async function downloadSlackAttachment(
  apiService: ApiService,
  attachmentId: string,
  slackTeamId?: string,
  conversationId?: string,
) {
  const response: any = validateUuid(attachmentId)
    ? await apiService.download(`/conversations/${conversationId}/files/${attachmentId}/download`, 'GET')
    : await apiService.download(
        `/slack/files/${attachmentId}/download${slackTeamId ? `?teamId=${slackTeamId}` : ''}`,
        'GET',
      );

  const contentDisposition: string = response.headers.get('content-disposition');
  const filename =
    contentDisposition
      .split(';')
      .find((p) => p.startsWith('filename'))
      ?.split('=')[1] ?? 'file.txt';

  const binaryData = [];

  binaryData.push(response.body);

  const downloadLink = document.createElement('a');

  downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'blob' }));
  downloadLink.setAttribute('download', filename);

  document.body.appendChild(downloadLink);

  downloadLink.click();
  downloadLink.remove();
}

export function getIconForConversationPriority(priority?: number) {
  switch (priority) {
    case CONVERSATION_PRIORITY_VALUES.LOW:
      return 'remove-outline';
    case CONVERSATION_PRIORITY_VALUES.MEDIUM:
      return 'reorder-two-outline';
    case CONVERSATION_PRIORITY_VALUES.HIGH:
      return 'reorder-three-outline';
    case CONVERSATION_PRIORITY_VALUES.CRITICAL:
      return 'reorder-four-outline';
  }

  return '';
}

export function getColorForConversationPriority(priority: number) {
  const colorMap = {
    [CONVERSATION_PRIORITY_VALUES.LOW]: 'success',
    [CONVERSATION_PRIORITY_VALUES.HIGH]: 'warning',
    [CONVERSATION_PRIORITY_VALUES.CRITICAL]: 'danger',
  };
  return colorMap[priority] ?? 'dark';
}

export function getDisplayTextForDuration(durationSecs: number) {
  const ONE_DAY = 60 * 60 * 24;
  const ONE_HOUR = 60 * 60;
  const ONE_MINUTE = 60;

  const days = Math.floor(durationSecs / ONE_DAY);
  const hours = Math.floor((durationSecs - days * ONE_DAY) / ONE_HOUR);
  const minutes = Math.floor((durationSecs - hours * ONE_HOUR - days * ONE_DAY) / ONE_MINUTE);

  const dayLabel = days ? `${days} day${days === 1 ? '' : 's'}` : '';
  const hourLabel = hours ? `${hours} hour${hours === 1 ? '' : 's'}` : '';
  const minuteLabel =
    (dayLabel || hourLabel) && minutes === 0
      ? ''
      : `${minutes === 0 ? '< 1' : minutes} minute${minutes <= 1 ? '' : 's'}`;

  return `${dayLabel} ${hourLabel} ${minuteLabel}`.trim();
}

export function getSlackLink(
  teamId: string,
  messageTs?: string,
  threadTs?: string,
  channelId?: string,
  slackTeamUrl?: string,
) {
  if (slackTeamUrl) {
    let baseUrl = `${slackTeamUrl}archives/${channelId}`;
    const query = [`cid=${channelId}`];

    if (messageTs) {
      baseUrl += `/p${messageTs.replace('.', '')}`;

      query.push(`thread_ts=${threadTs ?? messageTs}`);
    }

    return `${baseUrl}?${query.join('&')}`;
  }

  const baseUrl = `slack://channel`;
  const query = [`team=${teamId}`, `id=${channelId}`];

  if (messageTs) {
    query.push(`message=${messageTs}`);
  }

  if (threadTs || messageTs) {
    query.push(`thread_ts=${threadTs ?? messageTs}`);
  }

  return `${baseUrl}?${query.join('&')}`;
}

export function generateRandomString(length = 6) {
  return Math.random()
    .toString(36)
    .substring(2, length + 2);
}

function getNormalizedOperator(operatorType: string, operator: string): string {
  if (operatorType === 'eq') {
    return operator;
  } else if (operatorType === 'ne') {
    if (operator === 'contains') {
      return 'notContains';
    } else if (operator === 'in') {
      return 'notIn';
    } else if (operator === '==') {
      return '!=';
    } else if (operator === '>=') {
      return '<';
    } else if (operator === '<=') {
      return '>';
    } else if (operator === 'notContains') {
      return 'contains';
    } else if (operator === 'notIn') {
      return 'in';
    } else if (operator === '!=') {
      return '==';
    } else if (operator === '>') {
      return '<=';
    } else if (operator === '<') {
      return '>=';
    }
  }

  return operator;
}

export function getWhereClausesFromFilters(
  selectedFilterMap: SelectedFilterMap,
  currentUserId?: string,
): WhereClause[] {
  const where: WhereClause[] = [];
  const userFields = ['assignedToUserId', 'submitterUserId'];

  for (const [option, values] of selectedFilterMap.entries()) {
    const operatorType = option.operator ?? 'eq';

    if (option.id.startsWith('ticketTypeFields')) {
      const whereClausesForCustomField: WhereClause[] = [];
      // Assuming only one value entry for custom fields in the map for Inbox/Automations context

      for (const filterValue of values) {
        const fieldType = filterValue.additionalFields?.type;
        let baseOperator: WhereClause['operator'] = '==';
        let processedValue: any;

        switch (fieldType) {
          case CUSTOM_FIELD_TYPES.MULTI_SELECT:
          case CUSTOM_FIELD_TYPES.MULTI_USER_SELECT:
          case CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT:
            baseOperator = SIMPLE_CONDITIONS_OPERATORS.CONTAINS;

            // handle multi-select
            if (Array.isArray(filterValue.values)) {
              if (
                filterValue.values.length > 0 &&
                typeof filterValue.values[0] === 'object' &&
                filterValue.values[0] !== null &&
                'id' in filterValue.values[0]
              ) {
                processedValue = filterValue.values.map((v: any) => v.id);
              } else {
                processedValue = filterValue.values;
              }
            } else {
              console.warn('Expected array for multi-select filter value:', filterValue);
              processedValue = [];
            }
            if (!Array.isArray(processedValue)) {
              processedValue = [processedValue].filter((v) => v !== null && v !== undefined);
            }
            break;
          case CUSTOM_FIELD_TYPES.SINGLE_SELECT:
          case CUSTOM_FIELD_TYPES.USER_SELECT:
          case CUSTOM_FIELD_TYPES.USER_GROUP_SELECT:
            baseOperator = '==';

            if (typeof filterValue.values === 'object' && filterValue.values !== null && 'id' in filterValue.values) {
              processedValue = filterValue.values.id;
            } else {
              processedValue = filterValue.values;
            }
            break;
          case CUSTOM_FIELD_TYPES.CHECKBOX:
            baseOperator = '==';
            processedValue = Boolean(filterValue.values);
            break;
          case CUSTOM_FIELD_TYPES.DATE:
            baseOperator = '==';
            processedValue = filterValue.values;
            break;
          case CUSTOM_FIELD_TYPES.TEXT:
          default:
            baseOperator = '==';
            processedValue = String(filterValue.values);
            break;
        }

        if (
          processedValue !== undefined &&
          (!Array.isArray(processedValue) || processedValue.length > 0) &&
          filterValue.id !== null
        ) {
          const isArray = Array.isArray(processedValue);
          let valuesToSend = (isArray ? processedValue : [processedValue])?.map((v) => v?.id ?? v) ?? [];

          // --- SENTINEL REPLACEMENT --- START
          if (currentUserId && userFields.includes(option.id)) {
            valuesToSend = valuesToSend.map((val) => (val === CURRENT_USER_SENTINEL ? currentUserId : val));
          }
          // --- SENTINEL REPLACEMENT --- END

          whereClausesForCustomField.push({
            field: option.id,
            operator: getNormalizedOperator(operatorType, baseOperator) as WhereClause['operator'],
            value: isArray ? valuesToSend : valuesToSend[0],
          });
        }
      }
      // Return the array (possibly empty) for this custom field filter
      where.push(...whereClausesForCustomField);
    }

    // --- Standard Filter Logic ---
    if (option.id === 'externalTasks.isClosed') {
      for (const value of values) {
        if (value.id === EXTERNAL_TASK_STATE.HAS_INCOMPLETE_TASKS) {
          where.push({
            field: option.id,
            operator: getNormalizedOperator(operatorType, 'contains') as WhereClause['operator'],
            value: [false],
          });
        } else {
          where.push({
            field: option.id,
            operator: getNormalizedOperator(operatorType, 'notContains') as WhereClause['operator'],
            value: [false],
          });
        }
      }
      continue;
    }

    if (!option.type || option.type === 'select') {
      const isArrayContainsField = [
        'tags.id',
        'customer.tags.id',
        'account.tags.id',
        'slackChannels.id',
        'slackGroups.id',
        'scimGroups.id',
        'pagerDutyEscalationPolicies.id',
      ].includes(option.id);
      const baseOperator = isArrayContainsField ? 'contains' : 'in';

      if (option.grouping === 'and') {
        for (const f of values) {
          let valueToSend = isArrayContainsField ? [f.id] : f.id;

          // --- SENTINEL REPLACEMENT (needed here too if user fields could be 'AND'ed) ---
          // Note: This might not be strictly necessary if only tags use 'AND' grouping, but added for robustness
          if (currentUserId && userFields.includes(option.id)) {
            if (Array.isArray(valueToSend)) {
              valueToSend = valueToSend.map((val) => (val === CURRENT_USER_SENTINEL ? currentUserId : val));
            } else {
              valueToSend = valueToSend === CURRENT_USER_SENTINEL ? currentUserId : valueToSend;
            }
          }
          // --- SENTINEL REPLACEMENT END ---

          where.push({
            field: option.id,
            operator: getNormalizedOperator(
              operatorType,
              isArrayContainsField ? 'contains' : '==',
            ) as WhereClause['operator'],
            value: valueToSend,
          });
        }
      } else {
        // Handle default 'OR' grouping (most fields)
        let valuesToSend = values.map((v) => v.id);

        // --- SENTINEL REPLACEMENT --- START
        if (currentUserId && userFields.includes(option.id)) {
          valuesToSend = valuesToSend.map((val) => (val === CURRENT_USER_SENTINEL ? currentUserId : val));
        }
        // --- SENTINEL REPLACEMENT --- END

        where.push({
          field: option.id,
          operator: getNormalizedOperator(operatorType, baseOperator) as WhereClause['operator'],
          value: valuesToSend,
        });
      }
    }

    if (option.type === 'date') {
      // handle different value types
      // all relative dates will only have one entry in values
      // absolute dates will have one or two
      if (
        values.length === 1 &&
        OPTION_LABEL_TO_KEY[values[0].label] &&
        RELATIVE_DATE_OPTIONS.has(OPTION_LABEL_TO_KEY[values[0].label])
      ) {
        // handle relative date and early exit
        where.push({
          field: option.id,
          operator: getNormalizedOperator(operatorType, 'in') as WhereClause['operator'],
          value: values[0].id, // The ID here should contain the relative date info (e.g., JSON string)
        });
      } else {
        // otherwise, handle absolute date
        if (values[0]?.id) {
          where.push({
            field: option.id,
            operator: getNormalizedOperator(operatorType, '>=') as WhereClause['operator'],
            value: values[0].id,
          });
        }
        if (values[1]?.id) {
          where.push({
            field: option.id,
            operator: getNormalizedOperator(operatorType, '<=') as WhereClause['operator'],
            value: values[1].id,
          });
        }
      }
    }
  }

  return where;
}

export function getFiltersFromWhereClauses(
  whereClauses: any[],
  filterOptions: FilterOption[],
  lookups: Record<string, Record<string, string>>,
) {
  const filters = new Map();

  for (const filter of whereClauses ?? []) {
    const filterOption = filterOptions.find((f) => f.id === filter.field) as FilterOption;
    const lookupId = filterOption?.lookupId ?? filter.field;

    if (filterOption) {
      if (filter.operator === 'in') {
        filterOption.operator = FILTER_OPERATORS.EQUALS;
      } else if (filter.operator === 'contains') {
        filterOption.operator = FILTER_OPERATORS.EQUALS;
      } else if (filter.operator === '==') {
        filterOption.operator = FILTER_OPERATORS.EQUALS;
      } else if (filter.operator === '>=') {
        filterOption.operator = FILTER_OPERATORS.EQUALS;
      } else if (filter.operator === '<=') {
        filterOption.operator = FILTER_OPERATORS.EQUALS;
      } else if (filter.operator === 'notIn') {
        filterOption.operator = FILTER_OPERATORS.NOT_EQUALS;
      } else if (filter.operator === 'notContains') {
        filterOption.operator = FILTER_OPERATORS.NOT_EQUALS;
      } else if (filter.operator === '!=') {
        filterOption.operator = FILTER_OPERATORS.NOT_EQUALS;
      } else if (filter.operator === '>') {
        filterOption.operator = FILTER_OPERATORS.NOT_EQUALS;
      } else if (filter.operator === '<') {
        filterOption.operator = FILTER_OPERATORS.NOT_EQUALS;
      }
    }

    if (filter.field === 'externalTasks.isClosed') {
      const existingFilter = filters.get(filterOption as FilterOption);
      const externalTaskSelectedOption =
        filter.operator === 'contains'
          ? EXTERNAL_TASK_STATE.HAS_INCOMPLETE_TASKS
          : EXTERNAL_TASK_STATE.ALL_TASKS_COMPLETE;
      const transformedFilterValues = [
        {
          id: externalTaskSelectedOption,
          label: lookups[lookupId]?.[externalTaskSelectedOption],
        },
      ];

      // If a filter for this already exists, we have multiple entries, and need to "and" them
      if (existingFilter) {
        transformedFilterValues.push(...existingFilter);
        filterOption.grouping = 'and';
      }

      filters.set(filterOption as FilterOption, transformedFilterValues);
    } else if (!filterOption?.type || filterOption.type === 'select') {
      const values = Array.isArray(filter.value) ? filter.value : [filter.value];
      const existingFilter = filters.get(filterOption as FilterOption);
      const transformedFilterValues = values.map((v) => ({
        id: v,
        label: lookups[lookupId]?.[v] ?? v,
      }));

      // If a filter for this already exists, we have multiple entries, and need to "and" them
      if (existingFilter) {
        transformedFilterValues.push(...existingFilter);
        filterOption.grouping = 'and';
      }

      filters.set(filterOption as FilterOption, transformedFilterValues);
    } else if (filterOption.type === 'date') {
      const existingFilterValues = filters.get(filterOption as FilterOption) ?? [];

      if (filter.operator === '>=') {
        existingFilterValues[0] = {
          id: filter.value,
          label: lookups[lookupId]?.[filter.value] ?? filter.value,
        };
      } else if (filter.operator === '<=') {
        existingFilterValues[1] = {
          id: filter.value,
          label: lookups[lookupId]?.[filter.value] ?? filter.value,
        };
      } else if (filter.operator === 'in') {
        // this is a relative filter, so there is a JSONB payload in the value field
        try {
          const parsedDateConfig = JSON.parse(filter.value);
          const label = DATE_OPTION_LABELS[parsedDateConfig.key];

          if (!label) {
            console.warn('Unknown date option key:', parsedDateConfig.key);
            continue; // Skip this filter instead of adding invalid data
          }

          existingFilterValues[0] = {
            id: filter.value,
            label,
          };
        } catch (error) {
          console.warn('Failed to parse relative date filter JSON:', filter.value, error);
          continue; // Skip this filter instead of adding invalid data
        }
      }

      filters.set(filterOption as FilterOption, existingFilterValues);
    }
  }

  return filters;
}

export function getEmailDomain(email?: string) {
  return email?.split('@')?.[1] ?? email ?? '';
}

export function attachEventListenerToElement(
  query: string,
  type: string,
  listener: EventListenerOrEventListenerObject,
  options?: boolean | AddEventListenerOptions,
) {
  const element = document.querySelector(query);

  if (element) {
    element.addEventListener(type, listener, options);
  }

  return {
    remove: () => {
      if (element) {
        element.removeEventListener(type, listener, options);
      }
    },
  };
}

export function generateCustomFieldsArray(
  defaultFields: any[],
  currentFields: any[],
  additionalKeyValueFields: Record<string, unknown>,
) {
  const fields = [];
  defaultFields.forEach((defaultField) => {
    // Check if this is mapped to a field on the profile
    if (defaultField.fieldKey) {
      fields.push({
        ...defaultField,
        value: _.get(additionalKeyValueFields, defaultField.fieldKey, ''),
      });
      return;
    }

    let hasHit = false;
    if (currentFields) {
      currentFields.forEach((field) => {
        if (getCustomFieldId(defaultField) === getCustomFieldId(field)) {
          // Take the value given, but any other metadata (like required or visible) should inherit from company setting.
          // For multi-select or multiple choice, this is tricky because they may have
          //  changed the options and we want to maintain their selection
          const options = defaultField.options
            ? _.uniqBy([...(field?.options ?? []), ...(defaultField?.options ?? [])], (f) => f)
            : null;

          fields.push({
            ...defaultField,
            value: field?.value,
            options,
          });
          hasHit = true;
        }
      });
    }

    if (!hasHit) {
      fields.push({
        ...defaultField,
      });
    }
  });
  return fields;
}

export function getRandomColor(): string {
  const getRandomValue = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  // Generate random values for hue, saturation, and lightness
  // to produce more vibrant colors
  const hue = getRandomValue(0, 360);
  const saturation = getRandomValue(30, 70); // Higher saturation
  const lightness = getRandomValue(50, 70); // Moderate lightness for visibility

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

export function getMilitaryTime(time: string) {
  const [hourMinute, amPm] = time.split(' ');
  let [hour, minute] = hourMinute.split(':').map(Number);
  if (amPm.toLowerCase() === 'pm' && hour !== 12) {
    hour += 12;
  } else if (amPm.toLowerCase() === 'am' && hour === 12) {
    hour = 0;
  }
  return `${hour}:${minute < 10 ? '0' + minute : minute}`;
}

export function getAmPmFromMilitaryTime(time: string) {
  const [hours, minutes] = time.split(':');
  let hour = parseInt(hours);
  let period = 'AM';

  if (hour >= 12) {
    period = 'PM';
    if (hour > 12) {
      hour -= 12; // Convert 13-23 hours to 1-11 PM
    }
  }

  // Format the hour to not have leading zeros (e.g. "1" instead of "01")
  if (hour === 0) {
    hour = 12; // Midnight is 12 AM
  }

  const formattedHour = hour.toString();

  return `${formattedHour}:${minutes} ${period}`;
}

// used for angular compareWith
export function genericCompareFn(v1, v2) {
  return v1 === v2 || (v1 === '' && v2 === null);
}

export function fileNameFromImageModalUpload(uploadFile: Upload) {
  const fileName = uploadFile.file.name;
  const mimeType = uploadFile.file.type;

  // Extract extension from MIME type, with fallback to png
  let expectedExtension = mimeType?.split('/')?.[1] ?? 'png';

  // Handle special case for SVG (MIME type is 'image/svg+xml' but extension should be 'svg')
  if (expectedExtension === 'svg+xml') {
    expectedExtension = 'svg';
  }

  // Check if the filename already ends with the correct extension
  if (fileName.toLowerCase().endsWith(`.${expectedExtension.toLowerCase()}`)) {
    return fileName;
  }

  // Only add extension if it's not already there
  return `${fileName}.${expectedExtension}`;
}

export function getDatesFromDateRange(dateRange: any) {
  const startDate = moment().startOf(dateRange.period).subtract(dateRange.startIndex, dateRange.period).toDate();
  const endDate = moment().subtract(dateRange.endIndex, dateRange.period).endOf(dateRange.period).toDate();
  return [startDate, endDate];
}

export function createPriorityValueToTokenMap(
  labelsByKey: Record<string, PriorityTokens> = DEFAULT_PRIORITY_TOKENS,
): Record<number, PriorityTokens> {
  const map: Record<number, PriorityTokens> = {};
  for (const [key, value] of Object.entries(CONVERSATION_PRIORITY_VALUES)) {
    map[value] = labelsByKey[key] || DEFAULT_PRIORITY_TOKENS[key];
  }
  return map;
}

export function createPriorityValueToLabelMap(
  labelsByKey: Record<string, PriorityTokens> = DEFAULT_PRIORITY_TOKENS,
): Record<number, string> {
  const map: Record<number, string> = {};
  for (const [key, value] of Object.entries(CONVERSATION_PRIORITY_VALUES)) {
    map[value] = labelsByKey[key]?.LABEL || DEFAULT_PRIORITY_TOKENS[key].LABEL;
  }
  return map;
}

export function getOrderedPriorityOptions(
  labelsByKey: Record<string, PriorityTokens> = DEFAULT_PRIORITY_TOKENS,
): { value: number; label: string }[] {
  return Object.entries(CONVERSATION_PRIORITY_VALUES)
    .map(([key, value]) => ({
      value,
      label: labelsByKey[key]?.LABEL || DEFAULT_PRIORITY_TOKENS[key].LABEL,
    }))
    .sort((a, b) => a.value - b.value);
}

export function processPriorityLabelMap(formValue: Record<string, PriorityTokens>): Record<string, PriorityTokens> {
  const processedMap: Record<string, PriorityTokens> = {};

  // Iterate over the keys of DEFAULT_PRIORITY_LABEL_MAP_FORM to ensure all keys are present
  for (const key of Object.keys(DEFAULT_PRIORITY_LABEL_MAP_FORM)) {
    const inputConfig = formValue[key];

    const normalizeValue = (value: string | null | undefined): string | null => {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' ? null : trimmed;
      }
      return value ?? null; // Ensure undefined becomes null
    };

    processedMap[key] = {
      LABEL: normalizeValue(inputConfig.LABEL),
      EMOJI: normalizeValue(inputConfig.EMOJI),
      DESCRIPTION: normalizeValue(inputConfig.DESCRIPTION),
    };
  }
  return processedMap;
}

export function parseExternalSelectFunctionName(
  prefixedName: string,
  fallbackProjectId: string | null = null,
): { projectId: string | null; functionName: string } {
  if (!prefixedName || !prefixedName.includes('/')) {
    // Legacy unprefixed format
    return { projectId: fallbackProjectId, functionName: prefixedName };
  }

  const separatorIndex = prefixedName.indexOf('/');
  const prefix = prefixedName.substring(0, separatorIndex);
  const functionName = prefixedName.substring(separatorIndex + 1);

  // Empty prefix means tenant-level (null projectId)
  const projectId = prefix === '' ? null : prefix;

  return { projectId, functionName };
}

export function formatSlackChannelName(slackChannel?: SlackChannel) {
  if (!slackChannel) {
    return '';
  }

  if (slackChannel.name == null) {
    return PRIVATE_CHANNEL_NAME;
  }

  return `${slackChannel.name}${slackChannel.botIds != null && !slackChannel.botIds.length ? ` (disconnected)` : ''}`;
}

export function convertCamelCaseToSpaceSeparated(input: string) {
  if (!input) return '';

  return (
    input
      // Insert space before capital letters
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      // Handle cases like "JSONData" → "JSON Data"
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      // Split into words
      .split(' ')
      // Capitalize first letter of each word
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .trim()
  );
}

export function isDoNotReplyEmail(email?: string) {
  if (!email) {
    return false;
  }

  const NO_REPLY_EMAILS = ['no-reply', 'noreply', 'donotreply', 'do-not-reply'];
  return NO_REPLY_EMAILS.some((noReply) => email.toLowerCase().trim().startsWith(noReply));
}
